<template>
  <div class="quote-form bg-white px-4 py-4 font-lg">
    <form v-if="!isSubmitted" @submit.prevent="onSubmit" novalidate>
      <section class="mb-5">
        <h2 class="mb-0 text-center">Contact Information</h2>
        <hr />
        <div class="px-4">
          <b-row>
            <b-col sm="4" class="mb-3">
              <NanInput
                v-model="inputs.salutation"
                :validation="$v.inputs.salutation"
                :label="labels.salutation"
                type="text"
              />
            </b-col>
            <b-col sm="4" class="mb-3">
              <NanInput
                v-model="inputs.fname"
                :validation="$v.inputs.fname"
                :label="labels.fname"
                type="text"
                required
              />
            </b-col>
            <b-col sm="4" class="mb-3">
              <NanInput
                v-model="inputs.lname"
                :validation="$v.inputs.lname"
                :label="labels.lname"
                type="text"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6" class="mb-3">
              <NanInput
                v-model="inputs.email"
                :validation="$v.inputs.email"
                :label="labels.email"
                type="email"
                required
              />
            </b-col>
            <b-col sm="6" class="mb-3">
              <NanInput
                v-model="inputs.phone"
                :validation="$v.inputs.phone"
                :label="labels.phone"
                type="text"
              />
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="mb-5">
        <h2 class="mb-0 text-center">Your Institute</h2>
        <hr />
        <div class="px-4">
          <b-row>
            <b-col sm="12" class="mb-3">
              <NanInput
                v-model="inputs.institute"
                :validation="$v.inputs.institute"
                :label="labels.institute"
                type="text"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6" class="mb-3">
              <NanInput
                v-model="inputs.instituteType"
                :validation="$v.inputs.instituteType"
                :options="options.instituteType"
                :label="labels.instituteType"
                type="select"
                required
              />
            </b-col>
            <b-col sm="6" class="mb-3">
              <NanInput
                v-model="inputs.occupation"
                :validation="$v.inputs.occupation"
                :options="options.occupation"
                :label="labels.occupation"
                type="select"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="8" class="mb-3">
              <NanInput
                v-model="inputs.street"
                :validation="$v.inputs.street"
                :label="labels.street"
                type="text"
                required
              />
            </b-col>
            <b-col sm="4" class="mb-3">
              <NanInput
                v-model="inputs.postalCode"
                :validation="$v.inputs.postalCode"
                :label="labels.postalCode"
                type="number"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="8" class="mb-3">
              <NanInput
                v-model="inputs.city"
                :validation="$v.inputs.city"
                :label="labels.city"
                type="text"
                required
              />
            </b-col>
            <b-col sm="4" class="mb-3">
              <NanInput
                v-model="inputs.state"
                :validation="$v.inputs.state"
                :options="options.state"
                :label="labels.state"
                type="select"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col v-if="inputs.state === 'Other'" sm="6" class="mb-3">
              <NanInput
                v-model="inputs.otherState"
                :validation="$v.inputs.otherState"
                :options="options.otherState"
                :label="labels.otherState"
                type="text"
                required
              />
            </b-col>
            <b-col :sm="inputs.state === 'Other' ? 6 : 12" class="mb-3">
              <NanInput
                v-model="inputs.country"
                :validation="$v.inputs.country"
                :options="options.country"
                :label="labels.country"
                type="select"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="12" class="mb-3">
              <NanInput
                v-model="inputs.url"
                :validation="$v.inputs.url"
                :label="labels.url"
                type="text"
              />
            </b-col>
          </b-row>
        </div>
      </section>

      <section class="mb-5">
        <h2 class="mb-0 text-center">Licensing</h2>
        <hr />
        <div class="px-4">
          <div v-for="license in licenses" :key="license">
            <b-row>
              <b-col sm="8" class="mb-3">
                {{ labels.licenses[license] }}
              </b-col>
              <b-col sm="4" class="mb-3">
                <NanTicker v-model="inputs.licenses[license]" :max="200" />
              </b-col>
            </b-row>
            <b-row v-if="license === 'edu' && inputs.licenses[license] > 0">
              <b-col sm="12" class="mb-3">
                <NanInput
                  v-model="inputs.stacks"
                  :validation="$v.inputs.stacks"
                  :options="options.stacks"
                  :label="labels.stacks"
                  type="select"
                  required
                />
                <a class="small" href="https://nanome.ai/stacks" target="_blank"
                  >Learn more about Nanome Stacks</a
                >
              </b-col>
            </b-row>
          </div>
          <div
            v-if="$v.inputs.licenses.$error"
            class="mb-3 small text-center text-md-right text-danger"
          >
            You must choose at least 1 license.
          </div>
          <b-row>
            <b-col sm="12" class="mb-3">
              <NanInput
                v-model="inputs.privateServer"
                :validation="$v.inputs.privateServer"
                :label="labels.privateServer"
                :options="options.privateServer"
                type="select"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="12" class="mb-3">
              <NanInput
                v-model="inputs.experience"
                :validation="$v.inputs.experience"
                :options="options.experience"
                :label="labels.experience"
                type="select"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="12" class="mb-3">
              <NanInput
                v-model="inputs.reference"
                :validation="$v.inputs.reference"
                :options="options.reference"
                :label="labels.reference"
                type="select"
                required
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="12">
              <NanInput
                v-model="inputs.notes"
                :validation="$v.inputs.notes"
                :label="labels.notes"
                type="textarea"
                maxlength="1500"
              />
            </b-col>
          </b-row>
        </div>
      </section>

      <div class="px-4 my-4">
        <b-btn
          @click="$v.$touch"
          v-if="!isLoading"
          class="font-lg w-100"
          variant="primary"
          type="submit"
        >
          Get a Quote
        </b-btn>
        <b-btn v-else class="font-lg w-100" variant="primary" disabled>
          Please Wait
        </b-btn>
      </div>
    </form>
    <div v-else class="text-center py-2">
      <h2 class="h1">Request Submitted</h2>
      <p class="font-xl">
        Thank you for requesting a quote!<br />
        Our team will get back to you within 1-2 business days.
      </p>
      <b-button @click="reset" variant="primary"> Submit Another </b-button>
    </div>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  requiredIf,
  email,
  numeric,
  maxLength,
  url
} from "vuelidate/lib/validators";

import states from "@/data/states";
import countries from "@/data/countries";
import API from "@/api";

export default {
  mixins: [validationMixin],

  data: () => ({
    isSubmitted: false,
    isLoading: false,
    inputs: {
      salutation: "",
      fname: "",
      lname: "",
      email: "",
      phone: "",
      institute: "",
      instituteType: "",
      occupation: "",
      street: "",
      postalCode: "",
      city: "",
      state: "",
      otherState: "",
      country: "",
      url: "",
      licenses: {
        edu: 0,
        research: 0,
        enterprise: 0
      },
      stacks: "",
      privateServer: "",
      experience: "",
      reference: "",
      notes: ""
    },
    labels: {
      salutation: "Salutation",
      fname: "First Name",
      lname: "Last Name",
      email: "Email",
      phone: "Phone Number",
      institute: "Full name of your Institute",
      instituteType: "Type of Institute",
      occupation: "Occupation",
      street: "Street Address",
      postalCode: "Zip Code",
      city: "City",
      state: "State",
      otherState: "State (Other)",
      country: "Country",
      url: "Web Address",
      licenses: {
        edu: "Academic licenses",
        research: "Research licenses",
        enterprise: "Enterprise licenses"
      },
      stacks:
        "Are you interested in adding Nanome Stacks access to any of your licenses?",
      privateServer:
        "Are you interested in securing your proprietary data with a Private Server?",
      experience:
        "What is your experience with molecular visualization software?",
      reference: "How did you hear about Nanome?",
      notes: "Any additional notes?"
    },
    licenses: ["edu", "research", "enterprise"],
    options: {
      instituteType: [
        "",
        "University",
        "Government Lab",
        "Computing Center",
        "Company",
        "Other"
      ],
      occupation: [
        "",
        "IT Professional",
        "Computational Chemist",
        "Structural Biologist",
        "Medicinal Chemist",
        "Crystallographer",
        "Protein Engineer",
        "Educator",
        "Student",
        "Press",
        "Other"
      ],
      state: ["", ...states.map(c => c.name), "Other"],
      country: ["", ...countries.map(c => c.name)],
      stacks: ["", "Yes", "No"],
      privateServer: ["", "Yes", "No"],
      experience: [
        "",
        "None at all",
        "Less than a year",
        "1-2 years",
        "3-5 years",
        "More than 5 years"
      ],
      reference: [
        "",
        "Internet search",
        "Digital Publication",
        "Friends/Colleagues",
        "Used this software before",
        "Conference",
        "Reseller",
        "Facebook",
        "Twitter",
        "Instagram",
        "Reddit",
        "LinkedIn",
        "Medium",
        "Other"
      ]
    }
  }),

  async mounted() {
    for (let param in this.$route.query) {
      if (this.inputs.licenses[param] !== undefined) {
        this.inputs.licenses[param] = +this.$route.query[param];
      }
      if (param === "stacks") {
        this.inputs.stacks = "Yes";
      }
    }
  },

  methods: {
    reset() {
      for (const key in this.inputs) {
        if (key !== "licenses") {
          this.inputs[key] = "";
        }
      }
      this.inputs.stacks = false;
      for (const license in this.inputs.licenses) {
        this.inputs.licenses[license] = 0;
      }
      this.$v.$reset();
      this.isSubmitted = false;
    },

    async onSubmit() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }

      const inputs = { ...this.inputs };
      if (inputs.state === "Other") {
        inputs.state = inputs.otherState;
      }
      delete inputs.otherState;

      const labeledLicenses = {};
      for (const key in inputs.licenses) {
        labeledLicenses[this.labels.licenses[key]] = inputs.licenses[key];
      }
      delete inputs.licenses;

      // only show stacks message if at least 1 edu
      const includedInputs = Object.keys(inputs);
      if (this.inputs.licenses.edu === 0) {
        includedInputs.splice(includedInputs.indexOf("stacks"), 1);
      }

      const labeledInputs = includedInputs.reduce((results, input) => {
        const label = this.labels[input];
        results[label] = inputs[input];
        return results;
      }, {});

      const data = {
        ...labeledLicenses,
        ...labeledInputs
      };

      this.isLoading = true;
      const { success } = await API.requestQuote(data);
      this.isLoading = false;

      if (!success) {
        return this.$notify({
          type: "error",
          text: "There was an error submitting your quote request."
        });
      }

      this.isSubmitted = true;
    }
  },

  validations() {
    return {
      inputs: {
        salutation: { maxLength: maxLength(256) },
        fname: { required, maxLength: maxLength(256) },
        lname: { required, maxLength: maxLength(256) },
        email: { required, maxLength: maxLength(256), email },
        phone: { maxLength: maxLength(256), numeric },
        institute: { required, maxLength: maxLength(256) },
        instituteType: { required, maxLength: maxLength(256) },
        occupation: { required, maxLength: maxLength(256) },
        url: { url },
        street: { required, maxLength: maxLength(256) },
        postalCode: { maxLength: maxLength(256), numeric },
        city: { required, maxLength: maxLength(256) },
        state: { maxLength: maxLength(256) },
        otherState: {
          maxLength: maxLength(256),
          required: requiredIf(() => this.inputs.state === "Other")
        },
        country: { required, maxLength: maxLength(256) },
        stacks: { required: requiredIf(() => this.inputs.licenses.edu > 0) },
        privateServer: { required },
        experience: { required, maxLength: maxLength(256) },
        reference: { required, maxLength: maxLength(256) },
        notes: { maxLength: maxLength(1500) },
        licenses: {
          required: () => Object.values(this.inputs.licenses).some(v => v > 0)
        }
      }
    };
  }
};
</script>

<style lang="scss">
.quote-form {
  border-radius: 10px;
  box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.25);
}
</style>
